import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Stepper,
    Step,
    StepLabel,
    Button,
    Box,
    Typography,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Divider,
    IconButton,
    InputAdornment,
    Alert,
    Snackbar,
    Checkbox,
    FormControlLabel
} from '@mui/material';
import {
    Close as CloseIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon,
    CloudUpload as CloudUploadIcon
} from '@mui/icons-material';
import { signUp, submitInterestForm } from '../api_calls/api_calls_auth';

const GetStartedModal = ({ open, onClose }) => {
    const [activeStep, setActiveStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [signUpSuccess, setSignUpSuccess] = useState(false);
    const [interestFormSuccess, setInterestFormSuccess] = useState(false);
    const [interestFormLoading, setInterestFormLoading] = useState(false);

    // Interest form state
    const [interestForm, setInterestForm] = useState({
        fullName: '',
        contactPhone: '',
        address: '',
        postalCode: '',
        supplyType: '',
        electricalPanelPhoto: null,
        boiler_control: false
    });

    // Signup form state
    const [signupForm, setSignupForm] = useState({
        username: '',
        password: '',
        email: '',
        firstName: '',
        lastName: ''
    });
    const [showPassword, setShowPassword] = useState(false);
    const [errors, setErrors] = useState({});

    const steps = ['Introduction', 'Express Interest', 'Create Account'];

    const handleInterestFormSubmit = async () => {
        setErrorMessage('');
        setInterestFormLoading(true);

        try {
            const formData = new FormData();
            formData.append('full_name', interestForm.fullName);
            formData.append('phone', interestForm.contactPhone);
            formData.append('address', interestForm.address);
            formData.append('postal_code', interestForm.postalCode);
            formData.append('supply_type', interestForm.supplyType);
            formData.append('boiler_control', interestForm.boiler_control);

            if (interestForm.electricalPanelPhoto) {
                formData.append('panel_photo', interestForm.electricalPanelPhoto);
            }

            await submitInterestForm(formData);
            setInterestFormLoading(false);
            setInterestFormSuccess(true);
            setTimeout(() => {
                setActiveStep((prevActiveStep) => prevActiveStep + 1);
            }, 2000);
        } catch (error) {
            setInterestFormLoading(false);
            setErrorMessage('Something went wrong! Please try again!');
            console.error(error);
        }
    };

    const handleNext = () => {
        if (activeStep === 1) {
            // Validate interest form
            if (!interestForm.fullName || !interestForm.contactPhone || !interestForm.address ||
                !interestForm.postalCode || !interestForm.supplyType) {
                setErrorMessage('Please fill in all required fields.');
                return;
            }
            // Submit interest form
            handleInterestFormSubmit();
            return;
        }
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const handleClose = () => {
        setActiveStep(0);
        setInterestForm({
            fullName: '',
            contactPhone: '',
            address: '',
            postalCode: '',
            supplyType: '',
            electricalPanelPhoto: null,
            boiler_control: false
        });
        setSignupForm({
            username: '',
            password: '',
            email: '',
            firstName: '',
            lastName: ''
        });
        setErrors({});
        setErrorMessage('');
        onClose();
    };

    const handleSkipToSignup = () => {
        setActiveStep(2);
    };

    const handleInterestFormChange = (field, value) => {
        setInterestForm(prev => ({ ...prev, [field]: value }));
    };

    const handleSignupFormChange = (e) => {
        const { name, value } = e.target;
        setSignupForm(prev => ({ ...prev, [name]: value }));
        validateField(name, value);
    };

    const validateField = (name, value) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        let newErrors = { ...errors };

        switch (name) {
            case 'username':
            case 'password':
            case 'firstName':
            case 'lastName':
                newErrors[name] = value ? '' : true;
                break;
            case 'email':
                newErrors.email = value ? '' : true;
                newErrors.emailInvalid = value && !emailRegex.test(value) ? true : '';
                break;
            default:
                break;
        }

        setErrors(newErrors);
    };

    const validateSignupForm = () => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        let newErrors = {};

        if (!signupForm.username) newErrors.username = true;
        if (!signupForm.password) newErrors.password = true;
        if (!signupForm.email) newErrors.email = true;
        if (signupForm.email && !emailRegex.test(signupForm.email)) newErrors.emailInvalid = true;
        if (!signupForm.firstName) newErrors.firstName = true;
        if (!signupForm.lastName) newErrors.lastName = true;

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSignupClick = () => {
        if (validateSignupForm()) {
            handleSignup();
        } else {
            setErrorMessage('Please fill in all required fields.');
        }
    };

    const handleSignup = async () => {
        setErrorMessage('');
        setLoading(true);

        try {
            await signUp(signupForm);
            setLoading(false);
            setSignUpSuccess(true);
            setTimeout(() => {
                handleClose();
            }, 2000);
        } catch (error) {
            setLoading(false);
            setErrorMessage('Something went wrong! Please try again!');
            console.error(error);
        }
    };

    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        if (file && file.size <= 5 * 1024 * 1024) { // 5MB limit
            setInterestForm(prev => ({ ...prev, electricalPanelPhoto: file }));
        } else {
            setErrorMessage('File size must be less than 5MB');
        }
    };

    const renderStepContent = (step) => {
        switch (step) {
            case 0:
                return (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                        <Box sx={{
                            mt: 3,
                            mb: 3,
                            p: 3,
                            borderRadius: 3,
                            background: 'linear-gradient(135deg, rgba(25,118,210,0.08) 0%, rgba(64,196,255,0.05) 100%)',
                            border: '1px solid rgba(25,118,210,0.2)',
                            boxShadow: '0 4px 20px rgba(25,118,210,0.1)',
                            maxWidth: 700,
                            mx: 'auto'
                        }}>
                            <Typography sx={{
                                textAlign: 'center',
                                color: 'primary.main',
                                fontWeight: 600,
                                mb: 1,
                                fontSize: '1.5rem'
                            }}>
                                ✨ Welcome to Energy Home System
                            </Typography>
                            <Typography variant="body2" sx={{
                                textAlign: 'center',
                                color: 'text.secondary',
                                lineHeight: 1.6,
                                fontSize: '0.9rem',
                                mb: 2
                            }}>
                                By applying for an energy meter, you receive the device for free, allowing you to begin
                                recording your household's energy consumption in real time. Below, some basic details are
                                requested in order to carry out the installation. The information will not be shared with
                                third parties and will be used solely for the purpose of completing the installation.
                            </Typography>
                        </Box>
                    </Box>
                );

            case 1:
                return (
                    <Box sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom sx={{ mb: 1, fontWeight: 600, color: 'primary.main' }}>
                            Express Your Interest
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Provide your details to express interest in getting an energy meter installed.
                        </Typography>

                        {/* Skip message for users who already filled the form */}
                        <Box sx={{
                            mb: 1,
                            p: 2,
                            background: 'linear-gradient(135deg, rgba(25,118,210,0.08) 0%, rgba(64,196,255,0.05) 100%)',
                            border: '1px solid rgba(25,118,210,0.2)',
                            boxShadow: '0 4px 20px rgba(25,118,210,0.1)',
                            borderRadius: 2,

                        }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                Already filled out the contact form?
                            </Typography>
                            <Button
                                variant="text"
                                onClick={handleSkipToSignup}
                                sx={{
                                    textTransform: 'none',
                                    p: 0,
                                    minWidth: 'auto',
                                    color: 'secondary.main',
                                    fontWeight: 600
                                }}
                            >
                                You can create your account now! →
                            </Button>
                        </Box>

                        <Divider sx={{ mb: 3 }} />

                        {/* Form fields without individual cards */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <TextField
                                fullWidth
                                label="Full Name *"
                                value={interestForm.fullName}
                                onChange={(e) => handleInterestFormChange('fullName', e.target.value)}
                                placeholder="e.g., John Smith"
                                color="secondary"
                            />

                            <TextField
                                fullWidth
                                label="Contact Phone *"
                                value={interestForm.contactPhone}
                                onChange={(e) => handleInterestFormChange('contactPhone', e.target.value)}
                                placeholder="e.g., 210 1234567"
                                color="secondary"
                            />

                            <TextField
                                fullWidth
                                label="Address *"
                                value={interestForm.address}
                                onChange={(e) => handleInterestFormChange('address', e.target.value)}
                                placeholder="e.g., 123 Main Street, Athens"
                                color="secondary"
                            />

                            <TextField
                                fullWidth
                                label="Postal Code *"
                                value={interestForm.postalCode}
                                onChange={(e) => handleInterestFormChange('postalCode', e.target.value)}
                                placeholder="e.g., 10678"
                                color="secondary"
                            />

                            <FormControl fullWidth color="secondary">
                                <InputLabel>Supply Type *</InputLabel>
                                <Select
                                    value={interestForm.supplyType}
                                    onChange={(e) => handleInterestFormChange('supplyType', e.target.value)}
                                    label="Supply Type *"
                                    color="secondary"
                                >
                                    <MenuItem value="single-phase">Single Phase</MenuItem>
                                    <MenuItem value="three-phase">Three Phase</MenuItem>
                                    <MenuItem value="single-phase">I do not know</MenuItem>
                                </Select>
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                                    Depending on the household's supply, the corresponding energy meter is required.
                                </Typography>
                            </FormControl>

                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={interestForm.boiler_control}
                                        onChange={(e) => handleInterestFormChange('boiler_control', e.target.checked)}
                                        color="secondary"
                                    />
                                }
                                label="I want remote control capability for my boiler"
                                sx={{ mt: 2 }}
                            />

                            <Box sx={{ mt: 2 }}>
                                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                                    Electrical Panel Photo
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    A photo of the main electrical panel of the house helps in understanding the available
                                    space and if it's feasible to install.
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                                    Upload 1 supported file. Maximum 5 MB.
                                </Typography>
                                <Button
                                    variant="outlined"
                                    component="label"
                                    startIcon={<CloudUploadIcon />}
                                    sx={{ textTransform: 'none' }}
                                    color="secondary"
                                >
                                    Add File
                                    <input
                                        type="file"
                                        hidden
                                        accept="image/*"
                                        onChange={handleFileUpload}
                                    />
                                </Button>
                                {interestForm.electricalPanelPhoto && (
                                    <Typography variant="caption" sx={{ ml: 2, color: 'success.main' }}>
                                        File selected: {interestForm.electricalPanelPhoto.name}
                                    </Typography>
                                )}
                            </Box>
                        </Box>

                        {/* Success Alert */}
                        {interestFormSuccess && (
                            <Alert
                                variant="filled"
                                severity="success"
                                sx={{ mt: 3 }}
                                onClose={() => setInterestFormSuccess(false)}
                            >
                                Interest form submitted successfully! Proceeding to account creation...
                            </Alert>
                        )}
                    </Box>
                );

            case 2:
                return (
                    <Box sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                            Create Your Account
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Complete your registration to access your energy management dashboard.
                        </Typography>

                        {['username', 'password', 'email', 'firstName', 'lastName'].map((field) => {
                            const labelMap = {
                                username: 'Username',
                                password: 'Password',
                                email: 'Email',
                                firstName: 'First name',
                                lastName: 'Last name',
                            };
                            return (
                                <TextField
                                    key={field}
                                    onChange={handleSignupFormChange}
                                    variant="outlined"
                                    margin="dense"
                                    required
                                    fullWidth
                                    id={field}
                                    type={field === 'password' && !showPassword ? 'password' : 'text'}
                                    label={labelMap[field]}
                                    name={field}
                                    autoComplete={field}
                                    color="secondary"
                                    error={!!errors[field] || (field === 'email' && errors.emailInvalid)}
                                    helperText={(errors[field] && field === 'email') ? errors[field] : ''}
                                    InputProps={field === 'password' ? {
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton onClick={() => setShowPassword(!showPassword)}>
                                                    {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    } : null}
                                />
                            );
                        })}
                    </Box>
                );

            default:
                return 'Unknown step';
        }
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        minHeight: '600px',
                        display: 'flex',
                        flexDirection: 'column'
                    }
                }}
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h5" component="div" sx={{ color: 'primary.main', fontWeight: 'bold', my: 2 }}>
                        Get Started
                    </Typography>
                    <IconButton onClick={handleClose}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
                        {steps.map((label) => (
                            <Step key={label}>
                                <StepLabel>{label}</StepLabel>
                            </Step>
                        ))}
                    </Stepper>

                    <Box sx={{ flex: 1 }}>
                        {renderStepContent(activeStep)}
                    </Box>

                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        mt: 4,
                        pt: 2,
                        borderTop: '1px solid',
                        borderColor: 'divider'
                    }}>
                        <Button
                            disabled={activeStep === 0}
                            onClick={handleBack}
                            sx={{ textTransform: 'none' }}
                            color="primary"
                        >
                            Back
                        </Button>

                        {activeStep === steps.length - 1 ? (
                            <Button
                                variant="contained"
                                onClick={handleSignupClick}
                                disabled={loading}
                                sx={{ textTransform: 'none' }}
                                color="primary"
                            >
                                {loading ? 'Creating Account...' : 'Create Account'}
                            </Button>
                        ) : (
                            <Button
                                variant="contained"
                                onClick={handleNext}
                                disabled={activeStep === 1 && interestFormLoading}
                                sx={{ textTransform: 'none' }}
                                color="primary"
                            >
                                {activeStep === 1 && interestFormLoading ? 'Submitting...' : 'Next'}
                            </Button>
                        )}
                    </Box>
                </DialogContent>
            </Dialog>

            <Snackbar open={signUpSuccess} autoHideDuration={3000} onClose={() => setSignUpSuccess(false)}>
                <Alert variant="filled" onClose={() => setSignUpSuccess(false)} severity="success">
                    Account created successfully! Welcome to Energy Home System.
                </Alert>
            </Snackbar>


            <Snackbar open={!!errorMessage} autoHideDuration={6000} onClose={() => setErrorMessage('')}>
                <Alert variant="filled" onClose={() => setErrorMessage('')} severity="error">
                    {errorMessage}
                </Alert>
            </Snackbar>
        </>
    );
};

export default GetStartedModal;
